<template>
  <div class="w-full h-screen overflow-hidden bg-slate-100">
    <Topbar />

    <main class="flex h-[calc(100vh-64px)] flex-col overflow-hidden px-4 pb-5 pt-[84px] md:px-5 lg:px-6">
      <section class="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Напоминания по клиентам</h1>
          <p class="mt-1 text-sm text-slate-500 md:text-base">Сегодня, просроченные и будущие задачи для связи с клиентами</p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Button label="Обновить" icon="pi pi-refresh" severity="secondary" outlined @click="fetchReminders()" />
          <Button label="Добавить" icon="pi pi-plus" severity="success" @click="openCreateDialog" />
        </div>
      </section>

      <section class="mb-4 flex shrink-0 flex-wrap items-center gap-2">
        <Button
          v-for="scope in scopeOptions"
          :key="scope.value"
          :label="scope.label"
          :severity="activeScope === scope.value ? 'success' : 'secondary'"
          :outlined="activeScope !== scope.value"
          size="small"
          @click="activeScope = scope.value"
        />
      </section>

      <section class="mb-4 grid shrink-0 grid-cols-1 gap-3 md:grid-cols-3">
        <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p class="text-sm text-slate-500">Сегодня</p>
          <p class="mt-2 text-3xl font-semibold text-slate-900">{{ stats.today }}</p>
        </article>
        <article class="rounded-2xl border border-rose-200 bg-white p-4 shadow-sm">
          <p class="text-sm text-slate-500">Просрочено</p>
          <p class="mt-2 text-3xl font-semibold text-rose-700">{{ stats.overdue }}</p>
        </article>
        <article class="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm">
          <p class="text-sm text-slate-500">Выполнено</p>
          <p class="mt-2 text-3xl font-semibold text-emerald-700">{{ stats.done }}</p>
        </article>
      </section>

      <section class="min-h-0 flex-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <IconField class="w-full md:max-w-sm">
            <InputIcon><i class="pi pi-search" /></InputIcon>
            <InputText v-model.trim="searchQuery" class="w-full" placeholder="Поиск по клиенту, телефону или названию..." />
          </IconField>

          <Select
            v-model="selectedStatus"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full md:w-52"
          />
        </div>

        <div v-if="loading" class="py-10 text-center text-sm text-slate-500">Загрузка напоминаний...</div>
        <div v-else-if="!filteredReminders.length" class="py-10 text-center text-sm text-slate-500">Напоминаний не найдено</div>

        <div v-else class="min-h-0 h-full overflow-auto pr-1 space-y-3">
          <article
            v-for="reminder in filteredReminders"
            :key="reminder.id"
            class="rounded-2xl border px-4 py-3 shadow-sm"
            :class="getReminderCardClass(reminder)"
          >
            <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="text-base font-semibold text-slate-900">{{ reminder.title }}</p>
                  <span class="rounded-full px-2.5 py-1 text-xs font-medium" :class="getReminderTypeClass(reminder.type)">
                    {{ getReminderTypeLabel(reminder.type) }}
                  </span>
                  <span class="rounded-full px-2.5 py-1 text-xs font-medium" :class="getReminderStatusClass(reminder.status)">
                    {{ getReminderStatusLabel(reminder.status) }}
                  </span>
                </div>

                <p class="mt-2 text-sm text-slate-600">{{ reminder.client_name || 'Без клиента' }}</p>
                <p class="mt-1 text-xs text-slate-500">{{ formatPhone(reminder.client_phone) || 'Телефон не указан' }}</p>
                <p v-if="reminder.description" class="mt-2 text-sm text-slate-500">{{ reminder.description }}</p>
              </div>

              <div class="shrink-0 text-left md:text-right">
                <p class="text-sm font-medium text-slate-900">{{ formatDateTime(reminder.due_at) }}</p>
                <p class="mt-1 text-xs" :class="reminder.is_overdue ? 'text-rose-600' : 'text-slate-500'">
                  {{ getReminderDueLabel(reminder) }}
                </p>
                <div class="mt-3 flex flex-wrap gap-2 md:justify-end">
                  <Button
                    v-if="reminder.status === 'pending'"
                    label="Выполнено"
                    icon="pi pi-check"
                    size="small"
                    severity="success"
                    outlined
                    @click="completeReminder(reminder)"
                  />
                  <Button
                    label="Удалить"
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    outlined
                    @click="removeReminder(reminder)"
                  />
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </main>
  </div>

  <Dialog
    v-model:visible="createDialogVisible"
    modal
    header="Добавить напоминание"
    :style="{ width: '620px', maxWidth: '96vw' }"
  >
    <div class="space-y-4">
      <FloatLabel variant="on">
        <Select
          id="reminder-client"
          v-model="createForm.client_id"
          :options="clients"
          optionLabel="name"
          optionValue="id"
          filter
          :filterFields="['name', 'phone']"
          class="w-full"
        >
          <template #option="slotProps">
            <div class="flex w-full items-center justify-between gap-2">
              <span class="truncate">{{ slotProps.option.name }}</span>
              <span class="shrink-0 text-xs text-slate-500">{{ formatPhone(slotProps.option.phone) || '—' }}</span>
            </div>
          </template>
        </Select>
        <label for="reminder-client">Клиент</label>
      </FloatLabel>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FloatLabel variant="on">
          <InputText id="reminder-title" v-model.trim="createForm.title" class="w-full" />
          <label for="reminder-title">Название</label>
        </FloatLabel>

        <FloatLabel variant="on">
          <Select
            id="reminder-type"
            v-model="createForm.type"
            :options="typeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
          />
          <label for="reminder-type">Тип</label>
        </FloatLabel>
      </div>

      <FloatLabel variant="on">
        <InputText id="reminder-due-at" v-model="createForm.due_at" type="datetime-local" class="w-full" />
        <label for="reminder-due-at">Дата и время</label>
      </FloatLabel>

      <FloatLabel variant="on">
        <Textarea id="reminder-description" v-model.trim="createForm.description" rows="4" class="w-full" />
        <label for="reminder-description">Комментарий</label>
      </FloatLabel>
    </div>

    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <Button label="Отмена" severity="secondary" outlined @click="createDialogVisible = false" />
        <Button label="Сохранить" severity="success" :loading="saving" @click="createReminder" />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { computed, onMounted, ref, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import axios from '../lib/axios'
import Topbar from './Topbar.vue'

const toast = useToast()
const route = useRoute()
const loading = ref(false)
const saving = ref(false)
const reminders = ref([])
const clients = ref([])
const activeScope = ref('today')
const searchQuery = ref('')
const selectedStatus = ref('all')
const createDialogVisible = ref(false)

const selectedClientIdFromQuery = computed(() => {
  const raw = Number(route.query.client_id || 0)
  return Number.isFinite(raw) && raw > 0 ? raw : null
})

const scopeOptions = [
  { label: 'Сегодня', value: 'today' },
  { label: 'Просрочено', value: 'overdue' },
  { label: 'Предстоящие', value: 'upcoming' },
  { label: 'Все', value: 'all' },
]

const statusOptions = [
  { label: 'Все статусы', value: 'all' },
  { label: 'Ожидает', value: 'pending' },
  { label: 'Выполнено', value: 'done' },
  { label: 'Отменено', value: 'cancelled' },
]

const typeOptions = [
  { label: 'Звонок', value: 'call' },
  { label: 'Сообщение', value: 'message' },
  { label: 'День рождения', value: 'birthday' },
  { label: 'Повторный контакт', value: 'follow_up' },
  { label: 'Другое', value: 'custom' },
]

const createForm = ref({
  client_id: null,
  title: '',
  type: 'call',
  due_at: '',
  description: '',
})

const filteredReminders = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  return reminders.value.filter((item) => {
    const statusMatch = selectedStatus.value === 'all' || item.status === selectedStatus.value
    if (!statusMatch) return false

    if (!query) return true

    const haystack = [
      item.title,
      item.client_name,
      item.client_phone,
      item.description,
    ].filter(Boolean).join(' ').toLowerCase()

    return haystack.includes(query)
  })
})

const stats = computed(() => ({
  today: reminders.value.filter((item) => item.status === 'pending' && item.days_left === 0).length,
  overdue: reminders.value.filter((item) => item.status === 'pending' && item.is_overdue).length,
  done: reminders.value.filter((item) => item.status === 'done').length,
}))

const extractErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data
  const firstValidationError = responseData?.errors
    ? Object.values(responseData.errors).flat()[0]
    : null

  return firstValidationError || responseData?.message || fallbackMessage
}

const resetCreateForm = () => {
  const now = new Date()
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset())

  createForm.value = {
    client_id: selectedClientIdFromQuery.value,
    title: '',
    type: 'call',
    due_at: now.toISOString().slice(0, 16),
    description: '',
  }
}

const openCreateDialog = () => {
  resetCreateForm()
  createDialogVisible.value = true
}

const fetchClients = async () => {
  const { data } = await axios.get('/api/client')
  clients.value = Array.isArray(data) ? data : []
}

const fetchReminders = async () => {
  loading.value = true
  try {
    const { data } = await axios.get('/api/client-reminders', {
      params: {
        scope: activeScope.value,
        client_id: selectedClientIdFromQuery.value || undefined,
        limit: 200,
      },
    })
    reminders.value = Array.isArray(data) ? data : []
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: extractErrorMessage(error, 'Не удалось загрузить напоминания'), life: 4000 })
  } finally {
    loading.value = false
  }
}

const createReminder = async () => {
  saving.value = true
  try {
    await axios.post('/api/client-reminders', createForm.value)
    toast.add({ severity: 'success', summary: 'Сохранено', detail: 'Напоминание добавлено', life: 2500 })
    createDialogVisible.value = false
    await fetchReminders()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: extractErrorMessage(error, 'Не удалось создать напоминание'), life: 4000 })
  } finally {
    saving.value = false
  }
}

const completeReminder = async (reminder) => {
  try {
    await axios.post(`/api/client-reminders/${reminder.id}/complete`)
    toast.add({ severity: 'success', summary: 'Выполнено', detail: 'Напоминание закрыто', life: 2500 })
    await fetchReminders()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: extractErrorMessage(error, 'Не удалось закрыть напоминание'), life: 4000 })
  }
}

const removeReminder = async (reminder) => {
  try {
    await axios.delete(`/api/client-reminders/${reminder.id}`)
    toast.add({ severity: 'success', summary: 'Удалено', detail: 'Напоминание удалено', life: 2500 })
    await fetchReminders()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: extractErrorMessage(error, 'Не удалось удалить напоминание'), life: 4000 })
  }
}

const formatPhone = (value) => {
  const digits = String(value ?? '').replace(/\D+/g, '')
  if (!digits) return ''

  const normalized = digits.length === 10 ? `7${digits}` : digits
  if (normalized.length !== 11 || !normalized.startsWith('7')) {
    return String(value ?? '')
  }

  return `+${normalized[0]}(${normalized.slice(1, 4)})${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getReminderTypeLabel = (type) => ({
  call: 'Звонок',
  message: 'Сообщение',
  birthday: 'День рождения',
  follow_up: 'Повторный контакт',
  custom: 'Другое',
}[type] || 'Другое')

const getReminderStatusLabel = (status) => ({
  pending: 'Ожидает',
  done: 'Выполнено',
  cancelled: 'Отменено',
}[status] || 'Ожидает')

const getReminderTypeClass = (type) => ({
  call: 'bg-blue-100 text-blue-700',
  message: 'bg-violet-100 text-violet-700',
  birthday: 'bg-pink-100 text-pink-700',
  follow_up: 'bg-amber-100 text-amber-700',
  custom: 'bg-slate-100 text-slate-700',
}[type] || 'bg-slate-100 text-slate-700')

const getReminderStatusClass = (status) => ({
  pending: 'bg-amber-100 text-amber-700',
  done: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-600',
}[status] || 'bg-slate-100 text-slate-600')

const getReminderCardClass = (reminder) => {
  if (reminder.status === 'done') {
    return 'border-emerald-200 bg-emerald-50/50'
  }

  if (reminder.is_overdue) {
    return 'border-rose-200 bg-rose-50/50'
  }

  return 'border-slate-200 bg-slate-50/70'
}

const getReminderDueLabel = (reminder) => {
  if (reminder.status === 'done') {
    return 'Напоминание выполнено'
  }

  if (reminder.is_overdue) {
    return reminder.days_left === null ? 'Просрочено' : `Просрочено на ${Math.abs(Number(reminder.days_left || 0))} дн.`
  }

  if (Number(reminder.days_left || 0) === 0) {
    return 'На сегодня'
  }

  return `Через ${Number(reminder.days_left || 0)} дн.`
}

watch(activeScope, () => {
  fetchReminders()
})

watch(selectedClientIdFromQuery, () => {
  resetCreateForm()
  fetchReminders()
})

onMounted(async () => {
  resetCreateForm()
  await Promise.all([fetchClients(), fetchReminders()])
})
</script>
